/**
 * What a failed provisioning request (starting a trial) actually means.
 *
 * Provisioning runs in the request and outlasts ordinary ones. When the
 * request gives up - our own timeout, a dropped connection, a gateway timing
 * out in front of the API - the server usually carries on and finishes. The
 * page must keep waiting (following the signup status) rather than report a
 * network error and invite a second click on a reservation that is about to
 * be consumed.
 *
 * A 404/409 on a reservation the user just saw usually means an earlier
 * attempt (the one that "timed out") consumed it and built the workspace:
 * the page recovers into that workspace rather than showing a dead error.
 */

/** PendingSignup statuses the signup endpoints report (graphit-backend). */
export const SIGNUP_STATUS_PROVISIONING = "provisioning";
export const SIGNUP_STATUS_COMPLETED = "completed";

export const OUTCOME_WAIT = "wait";
export const OUTCOME_CONSUMED = "consumed";
export const OUTCOME_FAILED = "failed";

const CONSUMED_STATUSES = new Set([404, 409]);

/** Proxies answering for an API that is still working behind them. */
const GATEWAY_STATUSES = new Set([502, 503, 504]);

/**
 * @param {{status?: number, code?: string, isTimeout?: boolean}|null|undefined} apiError
 * @returns {"wait"|"consumed"|"failed"}
 */
export function classifyProvisioningError(apiError) {
  if (!apiError) return OUTCOME_FAILED;
  if (apiError.isTimeout || apiError.status === 0) return OUTCOME_WAIT;
  if (GATEWAY_STATUSES.has(apiError.status)) return OUTCOME_WAIT;
  if (CONSUMED_STATUSES.has(apiError.status)) return OUTCOME_CONSUMED;
  return OUTCOME_FAILED;
}
